import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi, api, mediaUrl } from "@/lib/api";
import { ImageField } from "./ImageField";

const EMPTY = {
  name: "",
  producer: "",
  category: "",
  country: "",
  description: "",
  logo: "",
  bottle_image: "",
  gallery: [],
  featured: false,
  sort_order: 0,
  active: true,
};

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | {..brand} (id => edit, no id => create)
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        adminApi.get("/admin/brands"),
        api.get("/categories"),
      ]);
      setBrands(b.data);
      setCategories(c.data);
    } catch {
      toast.error("Failed to load brands");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { id, ...body } = editing;
    body.sort_order = Number(body.sort_order) || 0;
    try {
      if (id) {
        await adminApi.put(`/admin/brands/${id}`, body);
        toast.success("Brand updated");
      } else {
        await adminApi.post("/admin/brands", body);
        toast.success("Brand created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (brand) => {
    if (!window.confirm(`Delete "${brand.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/brands/${brand.id}`);
      toast.success("Brand deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="admin-brands-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Brands</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Portfolio brands shown on the public website.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="bg-[#4B0F1E] hover:bg-[#3a0c17]" data-testid="brands-add-btn">
          <Plus size={15} className="mr-2" /> Add brand
        </Button>
      </div>

      <div className="mt-5 bg-white border border-neutral-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Sort</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {brands.map((b) => (
              <TableRow key={b.id} data-testid={`brand-row-${b.id}`}>
                <TableCell>
                  {b.logo || b.bottle_image ? (
                    <img src={mediaUrl(b.logo || b.bottle_image)} alt={b.name} className="h-9 w-9 object-contain" />
                  ) : (
                    <div className="h-9 w-9 bg-neutral-100 border border-neutral-200" />
                  )}
                </TableCell>
                <TableCell>
                  <span className="font-medium text-neutral-900 inline-flex items-center gap-1.5">
                    {b.name}
                    {b.featured && <Star size={12} className="text-amber-500 fill-amber-400" />}
                  </span>
                  {b.producer && <div className="text-xs text-neutral-500">{b.producer}</div>}
                </TableCell>
                <TableCell className="text-neutral-600">{b.category}</TableCell>
                <TableCell className="text-neutral-600">{b.country}</TableCell>
                <TableCell className="text-neutral-500">{b.sort_order}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={b.active ? "border-green-200 bg-green-50 text-green-700" : "border-neutral-200 text-neutral-400"}>
                    {b.active ? "Active" : "Hidden"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...EMPTY, ...b })} data-testid={`brand-edit-${b.id}`}>
                    <Pencil size={15} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(b)} data-testid={`brand-delete-${b.id}`}>
                    <Trash2 size={15} className="text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="brand-dialog">
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{editing.id ? "Edit brand" : "Add brand"}</DialogTitle>
                <DialogDescription>Brand details shown on the public portfolio.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <FieldText label="Name *" value={editing.name} onChange={set("name")} required testId="brand-name-input" />
                <FieldText label="Producer" value={editing.producer} onChange={set("producer")} testId="brand-producer-input" />
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Category *</Label>
                  <Select value={editing.category} onValueChange={set("category")} required>
                    <SelectTrigger data-testid="brand-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FieldText label="Country" value={editing.country} onChange={set("country")} testId="brand-country-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Description</Label>
                <Textarea rows={3} value={editing.description} onChange={(e) => set("description")(e.target.value)} data-testid="brand-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ImageField label="Logo" value={editing.logo} onChange={set("logo")} testId="brand-logo-upload" />
                <ImageField label="Bottle image" value={editing.bottle_image} onChange={set("bottle_image")} testId="brand-bottle-upload" />
              </div>
              <div className="grid grid-cols-3 gap-4 items-end">
                <FieldText label="Sort order" type="number" value={editing.sort_order} onChange={set("sort_order")} testId="brand-sort-input" />
                <label className="flex items-center gap-2 pb-2">
                  <Switch checked={editing.featured} onCheckedChange={set("featured")} data-testid="brand-featured-switch" />
                  <span className="text-sm text-neutral-600">Featured</span>
                </label>
                <label className="flex items-center gap-2 pb-2">
                  <Switch checked={editing.active} onCheckedChange={set("active")} data-testid="brand-active-switch" />
                  <span className="text-sm text-neutral-600">Active</span>
                </label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy || !editing.category} className="bg-[#4B0F1E] hover:bg-[#3a0c17]" data-testid="brand-save-btn">
                  {busy ? "Saving…" : "Save brand"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FieldText = ({ label, value, onChange, testId, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-neutral-500">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...props} />
  </div>
);
